import { useState } from 'react';
import { Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import type { Questionnaire, QuestionnaireQuestion, QuestionnaireSection } from '@peles/shared';
import { StatusBadge } from '../common';
import { QuestionEditModal, QUESTION_TYPE_LABELS } from './QuestionEditModal';
import {
  useAddQuestionnaireQuestion,
  useAddQuestionnaireSection,
  useDeleteQuestionnaireQuestion,
  useDeleteQuestionnaireSection,
  useUpdateQuestionnaireQuestion,
  useUpdateQuestionnaireSection,
} from '../../hooks/useQuestionnaires';
import { useDialog } from '../../context/DialogContext';

function dataLinkLabel(question: QuestionnaireQuestion): string | null {
  const link = question.dataLink;
  if (!link) return null;
  switch (link.kind) {
    case 'EVENT_FIELD':
      return `Event: ${link.field === 'startAt' ? 'date' : link.field === 'guestCount' ? 'guest count' : 'venue'}`;
    case 'EVENT_TAG':
      return `Tag: ${link.tagCode}`;
    case 'TIMELINE_TIME':
      return question.timelineEntryTitle
        ? `Timeline: ${question.timelineEntryTitle}`
        : `Timeline match: "${link.entryTitleMatch}"`;
    case 'VENDOR_LINK':
      return link.vendorCategory ? `Vendor: ${link.vendorCategory}` : 'Vendor list';
  }
}

export interface QuestionnaireEditorViewProps {
  questionnaire: Questionnaire;
}

/** Producer-side builder: manage a questionnaire's sections and questions. */
export function QuestionnaireEditorView({ questionnaire }: QuestionnaireEditorViewProps) {
  const { confirm } = useDialog();
  const [editingQuestion, setEditingQuestion] = useState<{
    section: QuestionnaireSection;
    question: QuestionnaireQuestion | null;
  } | null>(null);
  const [newSectionName, setNewSectionName] = useState('');

  const addSection = useAddQuestionnaireSection();
  const updateSection = useUpdateQuestionnaireSection(questionnaire.id);
  const deleteSection = useDeleteQuestionnaireSection(questionnaire.id);
  const addQuestion = useAddQuestionnaireQuestion(questionnaire.id);
  const updateQuestion = useUpdateQuestionnaireQuestion(questionnaire.id);
  const deleteQuestion = useDeleteQuestionnaireQuestion(questionnaire.id);

  function handleAddSection(e: React.FormEvent) {
    e.preventDefault();
    const name = newSectionName.trim();
    if (!name) return;
    addSection.mutate(
      {
        questionnaireId: questionnaire.id,
        name,
        sortOrder: questionnaire.sections.length,
      },
      { onSuccess: () => setNewSectionName('') },
    );
  }

  return (
    <div className="space-y-6">
      {questionnaire.sections.map((section) => (
        <div key={section.id} className="bg-white border border-gray-200 rounded-xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <EditableSectionName
              name={section.name}
              onRename={(name) => updateSection.mutate({ sectionId: section.id, name })}
            />
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEditingQuestion({ section, question: null })}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50"
                data-testid={`add-question-${section.id}`}
              >
                <Plus size={14} />
                Question
              </button>
              <button
                onClick={() => {
                  void (async () => {
                    if (
                      await confirm({
                        title: 'Delete Section',
                        message: `Delete section "${section.name}" and its questions?`,
                        confirmLabel: 'Delete',
                      })
                    ) {
                      deleteSection.mutate(section.id);
                    }
                  })();
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                aria-label={`Delete section ${section.name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          {section.description && (
            <p className="px-4 pt-3 text-sm text-gray-500">{section.description}</p>
          )}
          <ul className="divide-y divide-gray-50">
            {section.questions.map((question) => {
              const linkLabel = dataLinkLabel(question);
              return (
                <li key={question.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{question.prompt}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <StatusBadge
                        label={QUESTION_TYPE_LABELS[question.questionType]}
                        color="gray"
                      />
                      {question.isRequired && <StatusBadge label="Required" color="red" />}
                      {linkLabel && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                          <Link2 size={12} />
                          {linkLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setEditingQuestion({ section, question })}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-50"
                      aria-label={`Edit question ${question.prompt}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => {
                        void (async () => {
                          if (
                            await confirm({
                              title: 'Delete Question',
                              message: 'Delete this question?',
                              confirmLabel: 'Delete',
                            })
                          ) {
                            deleteQuestion.mutate(question.id);
                          }
                        })();
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                      aria-label={`Delete question ${question.prompt}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
            {section.questions.length === 0 && (
              <li className="px-4 py-4 text-sm text-gray-400">No questions yet.</li>
            )}
          </ul>
        </div>
      ))}

      <form onSubmit={handleAddSection} className="flex items-center gap-2">
        <input
          type="text"
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
          placeholder="New section name…"
          className="flex-1 max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="new-section-name"
        />
        <button
          type="submit"
          disabled={!newSectionName.trim() || addSection.isPending}
          className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
          data-testid="add-section-button"
        >
          <Plus size={16} />
          Add Section
        </button>
      </form>

      <QuestionEditModal
        open={editingQuestion !== null}
        onClose={() => setEditingQuestion(null)}
        question={editingQuestion?.question ?? null}
        eventId={questionnaire.eventId}
        eventTypeId={questionnaire.eventTypeId}
        timelineTemplateId={questionnaire.timelineTemplateId}
        submitting={addQuestion.isPending || updateQuestion.isPending}
        onSubmit={(input) => {
          if (!editingQuestion) return;
          if (editingQuestion.question) {
            updateQuestion.mutate(
              { questionId: editingQuestion.question.id, ...input },
              { onSuccess: () => setEditingQuestion(null) },
            );
          } else {
            addQuestion.mutate(
              {
                sectionId: editingQuestion.section.id,
                ...input,
                sortOrder: editingQuestion.section.questions.length,
              },
              { onSuccess: () => setEditingQuestion(null) },
            );
          }
        }}
      />
    </div>
  );
}

function EditableSectionName({
  name,
  onRename,
}: {
  name: string;
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  if (!editing) {
    return (
      <button
        onClick={() => {
          setValue(name);
          setEditing(true);
        }}
        className="text-sm font-semibold text-gray-900 hover:text-primary-700"
        title="Rename section"
      >
        {name}
      </button>
    );
  }
  return (
    <input
      autoFocus
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (value.trim() && value.trim() !== name) onRename(value.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setEditing(false);
      }}
      className="px-2 py-1 border border-gray-300 rounded text-sm font-semibold"
    />
  );
}
